import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';
import { getDataAPI, postDataAPI } from '../../utils/fetchData';

const BulkImportTourManager = () => {
  const navigate = useNavigate();
  const { modalConfig, showModal, showConfirm, closeModal } = useModal();
  const [bookings, setBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadConfirmedBookings();
  }, []);

  const loadConfirmedBookings = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/getBookings', token);
      
      if (response.data.status === 200 && response.data.data) {
        const bookingsData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
        // Filter only confirmed bookings that haven't been imported yet
        const confirmedBookings = bookingsData.filter(b => 
          b.status === 'confirmed' || b.status === 'announced'
        );
        setBookings(confirmedBookings);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      showModal('Load Error', 'Failed to load bookings. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleBooking = (bookingId) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const toggleAll = () => {
    if (selectedBookings.length === bookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(bookings.map(b => b._id));
    }
  };

  const handleImport = async () => {
    if (selectedBookings.length === 0) {
      showModal('No Selection', 'Please select at least one booking to import.', 'warning');
      return;
    }

    showConfirm(
      'Import Bookings',
      `Are you sure you want to import ${selectedBookings.length} booking${selectedBookings.length > 1 ? 's' : ''} as tour shows?`,
      async () => {
        try {
          setImporting(true);
          const token = JSON.parse(localStorage.getItem('dizeeToken'));
          
          // Get selected bookings data
          const selectedBookingsData = bookings.filter(b => selectedBookings.includes(b._id));
          
          console.log('Selected bookings:', selectedBookingsData);
          
          // Create tour shows from bookings
          const tourShows = selectedBookingsData.map(booking => {
            // Parse city_state (format: "City, State" or "City")
            let cityState = booking.city_state || '';
            
            // If city_state is empty but we have venue, try to extract city from it
            if (!cityState && booking.venue) {
              // Sometimes venue contains location info
              cityState = booking.venue;
            }
            
            const parts = cityState.split(',').map(p => p.trim());
            const city = parts[0] || '';
            const state = parts.length > 1 ? parts[1] : '';

            return {
              showDate: booking.perf_date,
              venue: {
                name: booking.venue || 'Unknown Venue',
                city: city || 'TBD',
                state: state,
                country: 'USA', // Default to USA, can be enhanced later
                address: ''
              },
              showInfo: {
                artist: booking.artist || 'Unknown Artist',
                showtimes: booking.schedule || '',
                doors: '',
                soundcheck: ''
              },
              status: 'planning', // Use valid enum value
              bookingId: booking._id,
              notes: booking.advanced_info || booking.notes || '',
              tags: []
            };
          });

          console.log('Tour shows to import:', tourShows);
          console.log('Sending to:', 'user/tm/shows/bulk');
          console.log('With token:', token ? 'Token present' : 'NO TOKEN');

          // Send to backend
          const response = await postDataAPI('user/tm/shows/bulk', { shows: tourShows }, token);
          
          console.log('Import response:', response);
          
          if (response.data.status === 200 || response.data.status === 201) {
            showModal('Success', `Successfully imported ${selectedBookings.length} tour show${selectedBookings.length > 1 ? 's' : ''}!`, 'success');
            setTimeout(() => {
              navigate('/tour-manager/view-all');
            }, 1500);
          } else {
            console.error('Import failed with status:', response.data.status, response.data);
            showModal('Import Error', `Failed to import: ${response.data.message || 'Unknown error'}`, 'error');
          }
        } catch (error) {
          console.error('Error importing shows:', error);
          console.error('Error details:', error.response?.data || error.message);
          const errorMsg = error.response?.data?.message || error.message || 'Please try again';
          showModal('Import Error', `Failed to import tour shows: ${errorMsg}`, 'error');
        } finally {
          setImporting(false);
        }
      },
      'info',
      'Import',
      'Cancel'
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Import Tour Shows</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {loading ? (
              <div className="text-center py-[60px]">
                <p className="text-white text-[12px]">Loading bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No confirmed bookings found
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  You need confirmed bookings to import as tour shows. Go to the Bookings app to add confirmed shows first.
                </p>
                <button 
                  onClick={() => navigate('/bookings')}
                  className="bg-[#FFFF00] text-black px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(255,255,0,0.9)] transition-colors"
                >
                  Go to Bookings
                </button>
              </div>
            ) : (
              <>
                {/* Info Box */}
                <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg p-5 mb-6">
                  <p className="text-[rgba(255,255,255,0.8)] text-[12px] leading-[1.6] m-0">
                    Select confirmed bookings to import as tour shows. Each show will have flight search, hotel booking, crew management, and guestlist features.
                  </p>
                </div>

                {/* Select All */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white text-[12px]">
                    {selectedBookings.length} of {bookings.length} selected
                  </span>
                  <button
                    onClick={toggleAll}
                    className="text-[#FF9500] text-[12px] hover:text-white transition-colors"
                  >
                    {selectedBookings.length === bookings.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* Bookings List */}
                <div className="space-y-3 mb-6">
                  {bookings.map(booking => (
                    <div
                      key={booking._id}
                      onClick={() => toggleBooking(booking._id)}
                      className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedBookings.includes(booking._id)
                          ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]'
                          : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,149,0,0.5)]'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-white text-[12px] font-semibold mb-1">
                            {booking.venue || 'Unknown Venue'}
                          </h3>
                          <p className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                            {booking.city}, {booking.state} • {formatDate(booking.perf_date)}
                          </p>
                          {booking.artist && (
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {booking.artist}
                            </p>
                          )}
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selectedBookings.includes(booking._id)
                            ? 'bg-[#FF9500] border-[#FF9500]'
                            : 'border-[rgba(255,255,255,0.3)]'
                        }`}>
                          {selectedBookings.includes(booking._id) && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Import Button */}
                {selectedBookings.length > 0 && (
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="w-full bg-[#FF9500] text-white border-none rounded-lg px-5 py-3 text-[12px] font-semibold cursor-pointer transition-colors hover:bg-[#FF8800] disabled:opacity-50 disabled:cursor-not-allowed mb-6"
                  >
                    {importing ? 'Importing...' : `Import ${selectedBookings.length} Show${selectedBookings.length > 1 ? 's' : ''}`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Modal */}
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default BulkImportTourManager;

